import { threadApi } from '@/lib/api/threads.api'

import type { CreateThreadComment, ThreadComment } from '@scrambl/shared'
import type {
  OfflineLedgerEntry,
  OfflineMutationEnvelope,
  OfflineRunnerContext,
} from '@scrambl/shared/offline'
import {
  CreateThreadCommentSchema,
  isValidUUID,
  ThreadCommentVisibilityEnum,
} from '@scrambl/shared'
import {
  assertOfflineWriteOwner,
  createScopedOfflineMutationFn,
  OfflineWriteKindError,
} from '@scrambl/shared/offline'
import { SessionChangedError } from '@scrambl/shared/session-scope'

import type { ThreadDraftOwner } from './drafts'
import { threadDraftOwnerKey } from './drafts'

export type ThreadCommentInput = Omit<
  CreateThreadComment,
  'parentCommentId'
> & {
  parentCommentId?: string | null
  moduleType?: string
  moduleId?: string
}
export type OwnedThreadCommentPayload = ThreadCommentInput & {
  commandVersion: 1
  commandOwner: ThreadDraftOwner
  authorSnapshot?: ThreadComment['author']
}
export function ownedCommentPayload(
  input: ThreadCommentInput,
  owner: ThreadDraftOwner,
  authorSnapshot?: ThreadComment['author'],
): OwnedThreadCommentPayload {
  if (!threadDraftOwnerKey(owner)) throw new OfflineWriteKindError()
  const clientMessageId = input.clientMessageId ?? crypto.randomUUID()
  const payload: OwnedThreadCommentPayload = {
    ...input,
    parentCommentId: input.parentCommentId ?? null,
    clientMessageId,
    commandVersion: 1,
    commandOwner: { ...owner },
    authorSnapshot,
    // Capture the original root visibility; retries cannot apply today's default.
    ...(input.parentCommentId
      ? {}
      : {
          visibility: owner.portalEntityId
            ? ThreadCommentVisibilityEnum.PUBLIC
            : (input.visibility ?? ThreadCommentVisibilityEnum.INTERNAL),
        }),
  }
  requireOwnedComment({
    version: 2,
    kind: 'thread-comment.create',
    ownerId: owner.userId,
    organizationId: owner.organizationId,
    idempotencyKey: clientMessageId,
    sessionGeneration: 0,
    enqueuedAt: Date.now(),
    label: 'Discussion message',
    payload,
  })
  return payload
}
export function requireOwnedComment(
  envelope: OfflineMutationEnvelope,
): OwnedThreadCommentPayload {
  const payload = envelope.payload as OwnedThreadCommentPayload | null
  if (
    envelope.kind !== 'thread-comment.create' ||
    !payload ||
    payload.commandVersion !== 1 ||
    !threadDraftOwnerKey(payload.commandOwner) ||
    payload.commandOwner.userId !== envelope.ownerId ||
    payload.commandOwner.organizationId !== envelope.organizationId ||
    !isValidUUID(payload.threadId) ||
    !isValidUUID(envelope.idempotencyKey) ||
    payload.clientMessageId !== envelope.idempotencyKey ||
    !CreateThreadCommentSchema.safeParse(payload).success ||
    (payload.authorSnapshot != null &&
      payload.authorSnapshot.id !== envelope.ownerId) ||
    (payload.parentCommentId != null &&
      !isValidUUID(payload.parentCommentId)) ||
    (!payload.parentCommentId &&
      !Object.values(ThreadCommentVisibilityEnum).includes(
        payload.visibility as ThreadCommentVisibilityEnum,
      ))
  )
    throw new OfflineWriteKindError()
  return payload
}
function requireCommentReply(
  row: ThreadComment,
  envelope: OfflineMutationEnvelope<OwnedThreadCommentPayload>,
): ThreadComment {
  if (
    !row ||
    !isValidUUID(row.id) ||
    row.organizationId !== envelope.organizationId ||
    row.authorId !== envelope.ownerId ||
    row.threadId !== envelope.payload.threadId ||
    row.clientMessageId !== envelope.idempotencyKey
  )
    throw new OfflineWriteKindError()
  return row
}

/** Use the shared ledger/FIFO/flush/retry/receipt rules for every dispatch path. */
export function createThreadCommentCommandFn(
  context: OfflineRunnerContext,
  selectedOwner: () => ThreadDraftOwner | null,
  signal?: AbortSignal,
) {
  return async (
    envelope: OfflineMutationEnvelope<OwnedThreadCommentPayload>,
  ): Promise<ThreadComment> => {
    assertOfflineWriteOwner(envelope, context.getOwnerId())
    if (envelope.kind !== 'thread-comment.create')
      throw new OfflineWriteKindError()
    if (envelope.organizationId !== selectedOwner()?.organizationId)
      throw new SessionChangedError()
    let payload: OwnedThreadCommentPayload
    try {
      payload = requireOwnedComment(envelope)
    } catch (error) {
      const original = context.ledger.get(
        envelope.idempotencyKey,
        envelope.ownerId,
      )
      if (
        original &&
        original.status !== 'succeeded' &&
        original.status !== 'conflicted'
      ) {
        context.ledger.patch(
          envelope.idempotencyKey,
          {
            status: 'failed',
            message:
              'The original message context cannot be verified. Keep it for review.',
          },
          envelope.ownerId,
        )
        await context.flush?.()
      }
      throw error
    }
    const ownerKey = threadDraftOwnerKey(selectedOwner())
    const capturedSession = context.captureSession?.()
    const assertCurrent = () => {
      capturedSession?.assertCurrent()
      signal?.throwIfAborted()
      if (
        !ownerKey ||
        ownerKey !== threadDraftOwnerKey(payload.commandOwner) ||
        ownerKey !== threadDraftOwnerKey(selectedOwner())
      )
        throw new SessionChangedError()
    }
    assertCurrent()
    const execute = createScopedOfflineMutationFn<
      OwnedThreadCommentPayload,
      ThreadComment
    >(
      'thread-comment.create',
      async original => {
        assertCurrent()
        const body = original.payload
        const result = await threadApi.createThreadComment(
          {
            threadId: body.threadId,
            parentCommentId: body.parentCommentId ?? null,
            content: body.content,
            clientMessageId: original.idempotencyKey,
            visibility: body.visibility,
            anchorId: body.anchorId,
            anchorText: body.anchorText,
          },
          { signal, portalEntityId: body.commandOwner.portalEntityId },
        )
        assertCurrent()
        return requireCommentReply(result, original)
      },
      {
        ...context,
        captureSession: () => ({
          assertCurrent,
          confirmCurrent:
            capturedSession?.confirmCurrent?.bind(capturedSession),
        }),
      },
      {
        organizationId: () => selectedOwner()?.organizationId ?? null,
        assertCurrent,
      },
    )
    const result = await execute(envelope)
    assertCurrent()
    return requireCommentReply(result, envelope)
  }
}
export type PendingThreadCommentEntry = Omit<OfflineLedgerEntry, 'envelope'> & {
  envelope: OfflineMutationEnvelope<OwnedThreadCommentPayload>
}
export function selectPendingThreadComments(
  entries: OfflineLedgerEntry[],
  owner: ThreadDraftOwner | null,
  threadId?: string | null,
): PendingThreadCommentEntry[] {
  const key = threadDraftOwnerKey(owner)
  if (!key) return []
  return entries.filter(entry => {
    if (
      entry.status === 'succeeded' ||
      entry.envelope.kind !== 'thread-comment.create'
    )
      return false
    try {
      const command = requireOwnedComment(entry.envelope)
      return (
        threadDraftOwnerKey(command.commandOwner) === key &&
        (!threadId || command.threadId === threadId)
      )
    } catch {
      return false
    }
  }) as PendingThreadCommentEntry[]
}

/** A leftover optimistic row cannot become another account's pending preview. */
export function isOwnedLocalCommentPreview(
  comment: {
    id: string
    clientMessageId?: string | null
    threadId: string
    organizationId: string
    authorId: string
    localStatus?: string
  },
  entries: PendingThreadCommentEntry[],
  owner: ThreadDraftOwner | null,
): boolean {
  if (!comment.localStatus) return true
  const receipt = comment.clientMessageId ?? comment.id
  return selectPendingThreadComments(entries, owner, comment.threadId).some(
    entry =>
      entry.envelope.idempotencyKey === receipt &&
      entry.envelope.ownerId === comment.authorId &&
      entry.envelope.organizationId === comment.organizationId,
  )
}
